import { useSearchParams } from "react-router-dom"

import { Button, Flex, Paragraph } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { isFramed, returnToClient } from "@/helpers/window.helpers"
import { useCreate, useHttp } from "@/hooks/useHttp"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { Heading } from "@/molecules/Heading"
import { ErrorResult } from "@/organisms/ErrorBoundary"
import { Loader } from "@/pages/SignInPage/components/molecules/Loader"

import styles from "./McpAuth.module.scss"

import type {
  AuthorizationDecision,
  AuthorizationRequest,
  AuthorizationRequestDetails
} from "./McpAuth.types"

/** An MCP client (Claude, Cursor, ...) asks to act as the signed-in user: show who asks, and let the user decide */
export const McpAuth = () => {
  const { t } = useTranslation("translation", { keyPrefix: "auth.mcpAuth" })
  const { logger } = useLogger()
  const [searchParams] = useSearchParams()
  const request = searchParams.get("request")

  const details = useHttp<AuthorizationRequestDetails>(
    RESOURCES_NAMES.AUTHENTICATION.MCP_REQUEST,
    { request },
    // handle a 4xx (invalid or expired request) in this component rather than the global error screen
    { enabled: !!request, retry: false, throwOnError: false }
  )

  const decide = useCreate<
    AuthorizationDecision,
    { needle: string; data: AuthorizationRequest }
  >(RESOURCES_NAMES.AUTHENTICATION.MCP, undefined, undefined, () => ({
    onSuccess: ({ redirectUrl }) => returnToClient(redirectUrl),
    onError: (error) =>
      logger.error(error, { tags: { flow: "mcp-oauth", action: "decision" } })
  }))

  if (!request || isFramed() || details.isError || decide.isError)
    return <ErrorResult placement="LAYOUT" errorType="Unknown" />
  if (!details.response || decide.isPending) return <Loader />

  const { clientName, redirectHost } = details.response
  const submit = (action: "approve" | "deny") =>
    decide.mutate({ needle: `/${action}`, data: { request } })

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={24}
      className={styles.wrapper}
    >
      <Heading level={2} title={t("title", { clientName })} />
      <Paragraph>{t("description", { clientName, redirectHost })}</Paragraph>
      <Paragraph>{t("trustWarning")}</Paragraph>
      <Flex gap={12}>
        <Button onClick={() => submit("deny")}>{t("deny")}</Button>
        <Button type="primary" onClick={() => submit("approve")}>
          {t("allow")}
        </Button>
      </Flex>
    </Flex>
  )
}
